import { SettingsDeleteButton } from 'App/Map/UserSettingsMenu/components/SettingsDeleteButton/SettingsDeleteButton'
import { SettingsDuplicateButton } from 'App/Map/UserSettingsMenu/components/SettingsDuplicateButton/SettingsDuplicateButton'
import { SettingsEditButton } from 'App/Map/UserSettingsMenu/components/SettingsEditButton/SettingsEditButton'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { colors } from 'settings/colors'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import { showConfirmSettingModal } from '../../../_utils/showConfirmSettingModal'
import type { WeeklyGeneralReport, WeeklyTrapCatchesReport, WeeklyTrapIssuesReport } from '../../_types'
import {
  WEEKLY_TRAP_CATCHES_REPORT_TYPE,
  WEEKLY_TRAP_ISSUES_REPORT_TYPE,
  WEEKLY_UPDATE_REPORT_TYPE,
  type TReportType,
} from '../../ReportForm/ReportType/ReportType'
import type { TModalDrawerForm } from '../../ReportsMain'

export const ReportCard = ({
  report,
  setSelectedReport,
  setModalDrawerForm,
  reportType,
  setReportType,
  setIsDuplicate,
  deleteReportHandler,
}: {
  setSelectedReport: Dispatch<
    SetStateAction<WeeklyGeneralReport | WeeklyTrapCatchesReport | WeeklyTrapIssuesReport | null>
  >
  setModalDrawerForm: Dispatch<SetStateAction<TModalDrawerForm>>
  reportType: TReportType
  report: WeeklyGeneralReport | WeeklyTrapCatchesReport | WeeklyTrapIssuesReport
  setReportType: Dispatch<SetStateAction<TReportType>>
  setIsDuplicate: Dispatch<SetStateAction<boolean>>
  deleteReportHandler: (id: string) => void
}) => {
  const {
    id,
    name,
    rules: { properties },
  } = report

  const handleDeleteAlert = async (id: string | null) => {
    try {
      if (!id) return

      if (reportType === WEEKLY_UPDATE_REPORT_TYPE) {
        const { errors } = await apiFetch({
          url: '/legacy-weekly-general-reports-management',
          body: {
            legacyWeeklyGeneralReportsToDelete: [Number(id)],
          },
        })

        if (errors.length) {
          throw new Error(errors.join(', '))
        }
      }

      if (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE) {
        const { errors } = await apiFetch({
          url: '/legacy-weekly-trap-catches-reports-management',
          body: {
            legacyWeeklyTrapCatchesReportsToDelete: [Number(id)],
          },
        })

        if (errors.length) {
          throw new Error(errors.join(', '))
        }
      }

      if (reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE) {
        const { errors } = await apiFetch({
          url: '/legacy-weekly-trap-issues-reports-management',
          body: {
            legacyWeeklyTrapIssuesReportsToDelete: [Number(id)],
          },
        })

        if (errors.length) {
          throw new Error(errors.join(', '))
        }
      }

      deleteReportHandler(id)

      showNotification({
        type: 'success',
        message: translate.phrases.banyanApp('Report successfully deleted'),
      })
    } catch (error) {
      showNotification({
        type: 'error',
        message: translate.phrases.banyanApp('Failed to delete report'),
      })
    }
  }

  return (
    <div
      css={{
        borderBottom: `1px solid ${colors.grey200}`,
        padding: 15,
        marginBottom: 5,
      }}
    >
      <div
        css={{
          display: 'flex',
          gridTemplateRows: 'auto',
          gridTemplateColumns: '1fr 0.25fr auto',
          justifyContent: 'space-between',
          justifyItems: 'start',
          flexWrap: 'nowrap',
        }}
      >
        <div css={{ fontWeight: 500 }}>{name}</div>
        <div
          css={{
            display: 'flex',
            marginLeft: '10px',
            paddingRight: '10px',
            minWidth: '110px',
            justifyContent: 'end',
          }}
        >
          {properties.length === 0
            ? translate.phrases.banyanApp('All Properties')
            : translate.phrases.banyanApp('{{count}} Properties', { count: properties.length })}
        </div>
      </div>
      <div
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'end',
          marginTop: '10px',
        }}
      >
        <SettingsDeleteButton
          onClick={() => {
            showConfirmSettingModal({
              title: translate.phrases.banyanApp('Delete report {{reportName}}', { reportName: name }),
              content: (
                <div>
                  <p>{translate.phrases.banyanApp('Are you sure you want to delete this report?')}</p>
                </div>
              ),
              onConfirm: () => handleDeleteAlert(id),
              confirmButtonText: translate.phrases.banyanApp('Delete Report'),
            })
          }}
        />
        <SettingsEditButton
          onClick={() => {
            setSelectedReport(report)

            setReportType(reportType)

            setModalDrawerForm('editReport')
          }}
        />
        <SettingsDuplicateButton
          onClick={() => {
            setReportType(reportType)

            setIsDuplicate(true)

            setSelectedReport({ ...report, id: null })

            setModalDrawerForm('createReport')
          }}
        />
      </div>
    </div>
  )
}
